import React, { useState, useEffect, useCallback } from "react";
import './PortfolioAnalyticsWithOptions.scss';
import { useDispatch, useSelector } from "react-redux";
import TagManager from "react-gtm-module";
import { FetchPortfolioAnalyticsWithOptions } from "../../Apis/FetchPortfolioAnalyticsWithOptions";
import PortfolioAnalyticsWithOptionsGraph from "./PortfolioAnalyticsWithOptionsGraph";
import { addZeroes } from "../../Utilities/AddZeros";
import { handleApiError } from "../../actions/notificationActions";
import { updatePdfData } from "../../actions/pdfDataActions";
import TooltipComponent from "../TooltipComponent/TooltipComponent";
import SkeletonComponent from '../Skeleton/Skeleton';
import PortfolioAnalyticsWithOptionsTable from "./PortfolioAnalyticsWithOptionsTable";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CaptureButtons from "../CaptureButtons/CaptureButtons";



function PortfolioAnalyticsWithOptions({ portfolioId, isEmpty, userType, CustomerID, hedgeableCount }) {
    var currObj = {
        style: "currency",
        currency: "USD",
    };
    
    const dispatch = useDispatch();
    const [bdopen, setBdOpen] = React.useState(true);
    const [indexTicker, setIndexTicker] = useState("SPX");
    
    const [portfolioMetrics, setPortfolioMetrics] = useState({});
    const [optionsData, setOptionsData] = useState([]);
    const [rawOptionsData, setRawOptionsData] = useState([]);
    const [equityData, setEquityData] = useState([]);
    const [rawEquityData, setRawEquityData] = useState([]);
    const [levelGridData, setLevelGridData] = useState([]);
    const [expectedTotalData, setExpectedTotalData] = useState([]);
    const [hasEquityData, setHasEquityData] = useState(false);
    const [hasOptionData, setHasOptionData] = useState(false);
    
    const [showAlmostThere, setShowAlmostThere] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const portfolioAnalyticsWithOptionsApiData = useSelector((state) => state.pdfData["Portfolio Analytics With Options"]);
    
    useEffect(() => {
        let timer;
        if (bdopen) {
            timer = setTimeout(() => {setShowAlmostThere(true);}, 1750);
        } else {
            setShowAlmostThere(false);
        }
        return () => clearTimeout(timer);
    }, [bdopen]);

    const processOptionsData = useCallback((optionData) => {
        return optionData.map((option, index) => ({
            sr: index + 1,
            symbol: option.ticker || '-',
            optionType: option.option_type || '-',
            type: option.option_type ? "Option".toUpperCase() : "Equity".toUpperCase() ||  '-',
            strikePrice: option.strike_price ? addZeroes(option.strike_price) : '-',
            expiryDate: option.expiration_date || '-',
            quantity: option.quantity ? option.quantity.toString() : '-',
            delta: option.delta_contribution !== undefined && option.delta_contribution !== null ? option.delta_contribution.toFixed(3) : '-',
            gamma: option.gamma_contribution !== undefined && option.gamma_contribution !== null ? option.gamma_contribution.toFixed(3) : '-',
            theta: option.theta_contribution !== undefined && option.theta_contribution !== null ? option.theta_contribution.toFixed(3) : '-',
            vega: option.vega_contribution !== undefined && option.vega_contribution !== null ? option.vega_contribution.toFixed(3) : '-',
            rho: option.rho_contribution !== undefined && option.rho_contribution !== null ? option.rho_contribution.toFixed(3) : '-',
            intrinsicValue: '-',
            nominalWeight: option.option_nominal_weight ? (option.option_nominal_weight * 100).toFixed(3) + '%' : '-',
            stockVolatility: option.historical_volatility ? (option.historical_volatility * 100).toFixed(3) + '%' : '-',
            stockBeta: option.beta ? option.beta.toFixed(3) : '-',
            iv: option.implied_volatility && option.implied_volatility !== '-' ? (option.implied_volatility * 100).toFixed(3) + '%' : (option.implied_volatility === '-' ? '-' : '-'),
            mtm: option.mtm ? addZeroes(option.mtm) : '-',
            riskcontribution: option.risk_contribution ? option.risk_contribution.toFixed(3) : '-',
            kHat: option.k_hat !== undefined && option.k_hat !== null && !isNaN(option.k_hat) ? Number(option.k_hat).toFixed(3) : '-',
            stockPrice: option.latest_close_price !== undefined && option.latest_close_price !== null && !isNaN(option.latest_close_price) ? Number(addZeroes(option.latest_close_price)).toFixed(3) : '-',
            indexBarrier: option.ladder_strike_index_price !== undefined && option.ladder_strike_index_price !== null && !isNaN(option.ladder_strike_index_price) ? Number(addZeroes(option.ladder_strike_index_price)).toFixed(3) : '-',
            pitmUsingTheBetaEquation: option.probability_option_itm ? (option.probability_option_itm * 100).toFixed(3) + '%' : '-'
        }));
    }, []);

    const processEquityData = useCallback((equityData) => {
        return equityData.map((equity, index) => ({
            sr: index + 1,
            symbol: equity.ticker || '-',
            quantity: equity.quantity ? equity.quantity.toString() : '-',
            type: equity.type ? equity.type.toUpperCase() : '-',
            tradeDate: equity.tradedate || '-',
            stockPrice: equity.latest_close_price ? addZeroes(equity.latest_close_price) : '-',
            latestClosePrice: equity.latest_close_price ? Number(addZeroes(equity.latest_close_price)).toFixed(3) : '-',
            marketValue: equity.market_value ? addZeroes(equity.market_value) : '-',
            nominalWeight: equity.weight ? (equity.weight * 100).toFixed(3) + '%' : '-',
            weight: equity.weight ? (equity.weight * 100).toFixed(3) + '%' : '-',
            stockVolatility: '-',
            stockBeta: '-',
            iv: '-',
            delta: '-',
            gamma: '-',
            theta: '-',
            kHat: '-',
            intrinsicValue: '-',
        }));
    }, []);

    const captureAndDispatchData = useCallback(async (response) => {
        if (response.status === 200) {
            dispatch(
                updatePdfData("Portfolio Analytics With Options", {
                    dataFlag: "Data Available",
                    allData: response,
                    portfolioid: portfolioId,
                })
            );
        } else {
            dispatch(
                updatePdfData("Portfolio Analytics With Options", {
                    dataFlag: "Data Not Available",
                    portfolioid: portfolioId,
                })
            );
        }
    }, [dispatch, portfolioId]);

    const setApiData = useCallback((response) => {
        setBdOpen(true);
        if (response.status === 200) {
            if (response.result?.message === "No index were identified") {
                setBdOpen(false);
                return;
            }
            const data = response.result;
            setPortfolioMetrics({
                upsideRisk: data.upside_risk || 0,
                downsideRisk: data.downside_risk || 0,
                portfolioDelta: data.portfolio_delta || 0,
                portfolioGamma: data.portfolio_gamma || 0,
                portfolioRho: data.portfolio_rho || 0,
                portfolioTheta: data.portfolio_theta || 0,
                portfolioVega: data.portfolio_vega || 0
            });
            
            const hasOptions = data.option_df && Array.isArray(data.option_df) && data.option_df.length > 0;
            const hasEquity = data.equity_df && Array.isArray(data.equity_df) && data.equity_df.length > 0;

            
            setHasOptionData(hasOptions);
            setHasEquityData(hasEquity);
            
            if (hasOptions) {
                setRawOptionsData(data.option_df);
                setOptionsData(processOptionsData(data.option_df));
            } else {
                setRawOptionsData([]);
                setOptionsData([]);
            }
            
            if (hasEquity) {
                setRawEquityData(data.equity_df);
                setEquityData(processEquityData(data.equity_df));
            } else {
                setRawEquityData([]);
                setEquityData([]);
            }
            
            if (data.level_grid) setLevelGridData(data.level_grid);
            if (data.expected_total) setExpectedTotalData(data.expected_total);
            
            TagManager.dataLayer({
                dataLayer: {
                    event: "Portfolio Analytics With Options Viewed",
                    userType,
                    user_id: CustomerID,
                },
            });
            
            setTimeout(() => captureAndDispatchData(response), 1000);
            setBdOpen(false);
        } else {
            handleApiError(response, dispatch);
            setTimeout(() => captureAndDispatchData(response), 1000);
            setBdOpen(false);
        }
    }, [userType, CustomerID, dispatch, captureAndDispatchData, processOptionsData, processEquityData]);

    const isApiDataPresent = useCallback(() => {
        return (
            portfolioAnalyticsWithOptionsApiData &&
            portfolioAnalyticsWithOptionsApiData.portfolioid === portfolioId &&
            portfolioAnalyticsWithOptionsApiData.dataFlag === "Data Available"
        );
    }, [portfolioAnalyticsWithOptionsApiData, portfolioId]);

    useEffect(() => {
        if (!isEmpty && portfolioId && (!isApiDataPresent())) {
            setBdOpen(true);
            setIsLoading(true);
            FetchPortfolioAnalyticsWithOptions(portfolioId, indexTicker)
                .then((response) => {
                    setApiData(response);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    setBdOpen(false);
                });
        } else if (isApiDataPresent()) {
            setApiData(portfolioAnalyticsWithOptionsApiData.allData);
        }
    }, [portfolioId, indexTicker, isEmpty]);

    const handleIndexChange = useCallback((event) => {
        const newIndex = event.target.value;
        if (newIndex !== indexTicker && !isEmpty && portfolioId) {
            setIndexTicker(newIndex);
            setBdOpen(true);
            setIsLoading(true);
            
            FetchPortfolioAnalyticsWithOptions(portfolioId, newIndex)
                .then((response) => {
                    if (response.status === 200 && response.result?.message !== "No index were identified") {
                        const data = response.result;
                        
                        setPortfolioMetrics({
                            upsideRisk: data.upside_risk || 0,
                            downsideRisk: data.downside_risk || 0,
                            portfolioDelta: data.portfolio_delta || 0,
                            portfolioGamma: data.portfolio_gamma || 0,
                            portfolioRho: data.portfolio_rho || 0,
                            portfolioTheta: data.portfolio_theta || 0,
                            portfolioVega: data.portfolio_vega || 0
                        });
                        
                        const hasOptions = data.option_df && Array.isArray(data.option_df) && data.option_df.length > 0;
                        const hasEquity = data.equity_df && Array.isArray(data.equity_df) && data.equity_df.length > 0;
                        
                        setHasOptionData(hasOptions);
                        setHasEquityData(hasEquity);
                        
                        if (hasOptions) {
                            setRawOptionsData(data.option_df);
                            setOptionsData(processOptionsData(data.option_df));
                        } else {
                            setRawOptionsData([]);
                            setOptionsData([]);
                        }
                        
                        if (hasEquity) {
                            setRawEquityData(data.equity_df);
                            setEquityData(processEquityData(data.equity_df));
                        } else {
                            setRawEquityData([]);
                            setEquityData([]);
                        }
                        
                        if (data.level_grid) setLevelGridData(data.level_grid);
                        if (data.expected_total) setExpectedTotalData(data.expected_total);
                        
                        captureAndDispatchData(response);
                    }
                    setIsLoading(false);
                    setBdOpen(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    setBdOpen(false);
                });
        }
    }, [indexTicker, isEmpty, portfolioId, CustomerID, processOptionsData, processEquityData, captureAndDispatchData]);

    const formatMetric = (value, type = 'number') => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        
        switch (type) {
            case 'percentage':
                return `${(value * 100).toFixed(3)}%`;
            case 'currency':
                return Number(addZeroes(Number(parseFloat(value).toFixed(3)))).toLocaleString("en-US", currObj);
            default:
                return value.toFixed(3);
        }
    };

    return (
        <>
            {!isEmpty ? (
                <>
                    {bdopen ? (
                        <div className="loading-overlay">
                            <SkeletonComponent variant="portfolioanalyticswithoptions" showAlmostThere={showAlmostThere} rows={optionsData.length > 6 ? 6 : optionsData.length} />
                        </div>
                    ) : (
                        <div className="drawercontent">
                            <div className='shieldrecc two'>
                                <div className='split'>
                                    <div className="card-container">
                                        <div className="header-card">
                                            <h3>Greeks vs Best Fit Index <TooltipComponent id={"Greeks vs Best Fit Index"} /></h3>
                                        </div>
                                        <div className="infocard fix">
                                            <Select
                                                className="ai-form-value-bt"
                                                value={indexTicker}
                                                onChange={handleIndexChange}
                                            >
                                                <MenuItem value="NDX">Nasdaq 100</MenuItem>
                                                <MenuItem value="SPX">S&P 500</MenuItem>
                                                <MenuItem value="RUT">Russell 2000</MenuItem>
                                            </Select>
                                            <span className="ai-backtest-select-arrow"></span>
                                        </div>
                                    </div>
                                    <div className="card-container sum-container" id="greeksvsbestfitindextable">
                                        <CaptureButtons 
                                            elementType='table' 
                                            chartName="Greeks_Vs_Best_Fit_Index_Table" 
                                            activeTab="Portfolio Analytics with Options" 
                                            tableHeaders={["DELTA", "GAMMA", "VEGA", "THETA", "RHO"]} 
                                            tableData={{
                                                "DELTA": formatMetric(portfolioMetrics.portfolioDelta),
                                                "GAMMA": formatMetric(portfolioMetrics.portfolioGamma),
                                                "VEGA": formatMetric(portfolioMetrics.portfolioVega),
                                                "THETA": formatMetric(portfolioMetrics.portfolioTheta),
                                                "RHO": formatMetric(portfolioMetrics.portfolioRho)
                                            }}
                                            userSelectedData={{"indexTicker": indexTicker}}
                                            id="greeksvsbestfitindextable">
                                        </CaptureButtons>
                                        <div className="header-card">
                                            <div>DELTA</div>
                                            <div>GAMMA</div>
                                            <div>VEGA</div>
                                            <div>THETA</div>
                                            <div>RHO</div>
                                        </div>
                                        {
                                            <div key={1} className="card small">
                                                 <>
                                                    <div>{formatMetric(portfolioMetrics.portfolioDelta)}</div>
                                                    <div>{formatMetric(portfolioMetrics.portfolioGamma)}</div>
                                                    <div>{formatMetric(portfolioMetrics.portfolioVega)}</div>
                                                    <div>{formatMetric(portfolioMetrics.portfolioTheta)}</div>
                                                    <div>{formatMetric(portfolioMetrics.portfolioRho)}</div>
                                                </>
                                            </div>
                                        }                        
                                    </div>
                                    <div className="sum-container" id="riskanalyticstable">
                                        <CaptureButtons 
                                            elementType='table' 
                                            chartName="Risk_Analytics_Table" 
                                            activeTab="Portfolio Analytics with Options" 
                                            tableHeaders={["DOWNSIDE SEMI-STD", "UPSIDE SEMI-STD"]} 
                                            tableData={{
                                                "DOWNSIDE SEMI-STD": formatMetric(portfolioMetrics.downsideRisk,'percentage'),
                                                "UPSIDE SEMI-STD": formatMetric(portfolioMetrics.upsideRisk,'percentage')
                                            }}
                                            userSelectedData={{"indexTicker": indexTicker}}
                                            id="riskanalyticstable">
                                        </CaptureButtons>
                                        <div className="card-container">
                                            <div className="header-card">
                                                <h3>Risk Analytics <TooltipComponent id={"Risk Analytics"} /></h3>
                                            </div>
                                        </div>
                                        
                                        <div className="card-container">
                                            <div className="header-card">
                                                {/* <div>IMPLIED VOLATILITY</div>
                                                <div>HISTORICAL</div> */}
                                                <div>DOWNSIDE SEMI-STD</div>
                                                <div>UPSIDE SEMI-STD</div>
                                            </div>
                                            {
                                                <div key={1} className="card small">
                                                    <>
                                                        {/* <div>{formatMetric(portfolioMetrics.implied_volatility,'percentage')}</div>
                                                        <div>{formatMetric(portfolioMetrics.historical_volatility,'percentage')}</div> */}
                                                        <div>{formatMetric(portfolioMetrics.downsideRisk,'percentage')}</div>
                                                        <div>{formatMetric(portfolioMetrics.upsideRisk,'percentage')}</div>
                                                    </>
                                                </div>
                                            }                        
                                        </div>
                                    </div>
                                </div>
                                <div id="portfolioanalyticswithoptionsgraph" className='split sum-container'>
                                <CaptureButtons elementType='chart' id='portfolioanalyticswithoptionsgraph' chartName="Portfolio_Analytics_With_Options" activeTab="Portfolio Analytics With Options" ></CaptureButtons>
                                    <PortfolioAnalyticsWithOptionsGraph 
                                        expectedTotalData={expectedTotalData} 
                                        levelGridData={levelGridData} 
                                        optionData={rawOptionsData} 
                                        indexTicker={indexTicker}
                                        hasOptionData={hasOptionData}
                                    />
                                </div>
                            </div>
                            <PortfolioAnalyticsWithOptionsTable 
                                optionsPortfolioTableData={optionsData}
                                hasEquityData={hasEquityData}
                                hasOptionData={hasOptionData}
                                equityPortfolioTableData={equityData} 
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="drawercontent">
                    <p>Portfolio is Empty. Please add stock to proceed</p>
                </div>
            )}
        </>
    );
}

export default PortfolioAnalyticsWithOptions;